import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputBase,
  InputLabel,
  Menu,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CommentIcon from "@mui/icons-material/Comment";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryIcon from "@mui/icons-material/History";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import DataTable from "../../components/brand/DataTable";
import Eyebrow from "../../components/brand/Eyebrow";
import { fonts } from "../../theme/tokens";
import {
  exportBudgetPdf,
  getAllocationMatrix,
  getBudgetLineItems,
  saveDiscretionaryAllocations,
  updateBudget,
  updateSectionAllocations,
  updateServiceChargeMechanism,
} from "../../api/financeApi";
import { getUnitGroups } from "../../api/unitApi";
import { downloadBlob, exportErrorMessage } from "../../utils/blobDownload";
import { getApiErrorMessage, isConcurrencyConflict } from "../../utils/concurrency";
import {
  MECHANISM_OPTIONS,
  budgetStatusColor,
  budgetStatusLabel,
  mechanismLabel,
  money,
  sanitizeMoney,
  sanitizePercent,
} from "./financeFormat";
import { fiscalYearLabel } from "./fiscalYearDates";
import { liveShare, syncDiscretionary } from "./syncDiscretionary";

const NAME_WIDTH = 260;
const PREV_WIDTH = 110;
const AMOUNT_WIDTH = 168;
const PERCENT_WIDTH = 148;
const POUND_WIDTH = 148;
const QUIET_WIDTH = 120;
const TOTAL_WIDTH = 120;

const gridTableSx = {
  borderCollapse: "separate",
  borderSpacing: 0,
  "& th, & td": { px: 1.75 },
  "& th:first-of-type, & td:first-of-type": { pl: 2.5 },
  "& th:last-of-type, & td:last-of-type": { pr: 2.5 },
  "& th": {
    textTransform: "none",
    letterSpacing: 0,
    fontSize: 13,
    fontWeight: 600,
    color: "brand.ink",
    bgcolor: "brand.surfaceMuted",
    borderBottom: (theme) => `1px solid ${theme.palette.brand.border}`,
    py: 1.25,
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    textAlign: "center",
  },
  "& thead th": { position: "sticky", top: 0, zIndex: 3 },
  "& thead tr.matrix-subhead th": { top: 40 },
  "& thead th.matrix-start": { left: 0, zIndex: 4 },
  "& th.matrix-start": { textAlign: "left" },
  "& th.matrix-end": { textAlign: "right" },
  "& tr.matrix-group th": {
    borderBottom: (theme) => `1px solid ${theme.palette.brand.borderStrong}`,
  },
  "& th.matrix-sub": {
    py: 0.75,
    fontSize: 11,
    fontWeight: 500,
    color: "brand.inkSoft",
  },
  "& td": {
    fontSize: 13,
    py: 1,
    borderBottom: (theme) => `1px solid ${theme.palette.brand.border}`,
    verticalAlign: "middle",
  },
  "& tbody tr:hover td:not(.matrix-category)": { bgcolor: "brand.surface" },
  "& td:hover .matrix-quiet, & td:focus-within .matrix-quiet": {
    bgcolor: "brand.surfaceMuted",
    "& .matrix-affix": { opacity: 1 },
    "& .MuiInputBase-input::placeholder": { opacity: 0.45 },
  },
  "& .matrix-split": { borderLeft: (theme) => `1px solid ${theme.palette.brand.borderStrong}`, pl: 2.25 },
  "& td.matrix-category": {
    py: 0,
    bgcolor: "brand.surfaceMuted",
    color: "brand.inkSoft",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
  },
  "& button.matrix-category-toggle": {
    display: "flex",
    alignItems: "center",
    width: "100%",
    m: 0,
    py: 0.75,
    px: 0,
    border: 0,
    bgcolor: "transparent",
    color: "inherit",
    font: "inherit",
    letterSpacing: "inherit",
    textTransform: "inherit",
    textAlign: "left",
    cursor: "pointer",
    "&:hover .matrix-category-label": { color: "brand.ink" },
  },
  "& .matrix-category-label": {
    position: "sticky",
    left: 20,
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
  },
  "& td.matrix-name": {
    position: "sticky",
    left: 0,
    zIndex: 1,
    bgcolor: "background.paper",
    boxShadow: (theme) => `1px 0 0 ${theme.palette.brand.border}`,
  },
  "& tbody tr:hover td.matrix-name": { bgcolor: "brand.surface" },
  "& tr.matrix-total td": {
    position: "sticky",
    bottom: 0,
    zIndex: 2,
    bgcolor: "brand.surfaceMuted",
    fontWeight: 600,
    borderTop: (theme) => `1px solid ${theme.palette.brand.borderStrong}`,
    borderBottom: "none",
  },
  "& tr.matrix-total td.matrix-name": { left: 0, zIndex: 3, bgcolor: "brand.surfaceMuted" },
};

function editKey(sectionId, lineId) {
  return `${sectionId}:${lineId}`;
}

function shareKey(sectionId, unitId) {
  return `${sectionId}:${unitId}`;
}

const fieldFrameSx = (unsaved, align = "end") => ({
  ml: "auto",
  mr: align === "center" ? "auto" : undefined,
  width: "fit-content",
  minWidth: 92,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 0.5,
  px: 1,
  py: 0.4,
  borderRadius: "6px",
  bgcolor: "brand.surfaceMuted",
  boxShadow: unsaved ? (theme) => `inset 3px 0 0 ${theme.palette.brand.gold}` : "none",
  transition: "background-color .12s, box-shadow .12s",
  "&:hover": { bgcolor: "brand.goldTint" },
  "&:focus-within": {
    bgcolor: "background.paper",
    boxShadow: (theme) => `0 0 0 1px ${theme.palette.brand.ink}`,
  },
});

const fieldInputSx = {
  width: "7.5ch",
  fontSize: 13,
  fontVariantNumeric: "tabular-nums",
  "& input": { p: 0, textAlign: "right", fontVariantNumeric: "tabular-nums" },
  "& input::placeholder": { opacity: 0.45 },
};

function completedZero(value) {
  if (value === "" || value == null) return false;
  const text = String(value);
  if (text.endsWith(".")) return false;
  return Number(text) === 0;
}

function MoneyCell({ value, label, readOnly, unsaved, blankZero, quietBlank, align = "end", placeholder = "0.00", onChange, onKeyDown, row, col }) {
  const [focused, setFocused] = useState(false);
  const blank = value === "" || value == null || (blankZero && completedZero(value));
  const quiet = Boolean(quietBlank) && blank && !focused;
  if (readOnly) {
    return (
      <Box component="span" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: blank ? 400 : 600, color: blank ? "brand.inkSoft" : "brand.ink" }}>
        {blank ? "—" : money(value)}
      </Box>
    );
  }
  return (
    <Box
      className={quiet ? "matrix-quiet" : undefined}
      sx={{
        ...fieldFrameSx(unsaved, align),
        ...(quiet ? {
          bgcolor: "transparent",
          boxShadow: "none",
          width: "100%",
          ml: 0,
          "&:hover": { bgcolor: "brand.surfaceMuted" },
          "& .matrix-affix, & .MuiInputBase-input::placeholder": { opacity: 0 },
          "&:hover .matrix-affix, &:focus-within .matrix-affix": { opacity: 1 },
          "&:hover .MuiInputBase-input::placeholder, &:focus-within .MuiInputBase-input::placeholder": { opacity: 0.45 },
        } : {}),
      }}
    >
      <Box className="matrix-affix" component="span" sx={{ color: "brand.inkSoft", fontSize: 12 }}>£</Box>
      <InputBase
        value={blank && !focused ? "" : value}
        aria-label={label}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(event) => onChange(sanitizeMoney(event.target.value))}
        inputProps={{
          inputMode: "decimal",
          "data-grid-row": row,
          "data-grid-col": col,
          onKeyDown,
        }}
        sx={{
          ...fieldInputSx,
          ...(quiet ? { flex: 1, width: "auto" } : {}),
          fontWeight: blank ? 400 : 600,
          color: blank ? "brand.inkSoft" : "brand.ink",
        }}
      />
    </Box>
  );
}

function scrollToId(event, id) {
  event.preventDefault();
  document.getElementById(id)?.scrollIntoView({ block: "nearest", inline: "center" });
}

function PercentCell({ value, label, readOnly, align = "end", onChange, onKeyDown, row, col }) {
  const blank = value === "" || value == null;
  if (readOnly) {
    return <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>{blank ? "—" : `${value}%`}</Box>;
  }
  return (
    <Box sx={{ ...fieldFrameSx(false, align), minWidth: 76 }}>
      <InputBase
        value={value ?? ""}
        aria-label={label}
        placeholder="0"
        onChange={(event) => onChange(sanitizePercent(event.target.value))}
        inputProps={{
          inputMode: "decimal",
          "data-grid-row": row,
          "data-grid-col": col,
          onKeyDown,
        }}
        sx={fieldInputSx}
      />
      <Box component="span" sx={{ color: "brand.inkSoft", fontSize: 12 }}>%</Box>
    </Box>
  );
}

function moveGridFocus(event) {
  if (!["Enter", "ArrowDown", "ArrowUp"].includes(event.key)) return;
  const row = Number(event.target.dataset.gridRow);
  const col = event.target.dataset.gridCol;
  if (!col || Number.isNaN(row)) return;
  const nextRow = event.key === "ArrowUp" ? row - 1 : row + 1;
  const next = document.querySelector(`[data-grid-row="${nextRow}"][data-grid-col="${col}"]`);
  if (!next) return;
  event.preventDefault();
  next.focus();
}

export default function BudgetMatrix({
  propertyId,
  budget,
  budgets,
  onSelectBudget,
  onCreate,
  onDelete,
  onStatus,
  onOwnerVisibility,
  onReload,
  showToast,
  setError,
  showPrevious,
  onTogglePrevious,
  scrollToWhoPays,
  onDirtyChange,
}) {
  const readOnly = budget?.status === "published";
  const [catalogue, setCatalogue] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [amountEdits, setAmountEdits] = useState({});
  const [shareEdits, setShareEdits] = useState({});
  const [removedSectionIds, setRemovedSectionIds] = useState([]);
  const [addedSections, setAddedSections] = useState([]);
  const [mechanismEdits, setMechanismEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [failedSectionId, setFailedSectionId] = useState(null);
  const [menu, setMenu] = useState(null);
  const [blockName, setBlockName] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [estateConfirm, setEstateConfirm] = useState(null);
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [reasonEdit, setReasonEdit] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [deleteSection, setDeleteSection] = useState(null);
  const [removeAllSection, setRemoveAllSection] = useState(null);
  const [collapsedCategories, setCollapsedCategories] = useState(() => new Set());

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    getBudgetLineItems()
      .then((res) => {
        if (!cancelled) setCatalogue(res.data.results || []);
      })
      .catch((error) => setError?.(getApiErrorMessage(error, "Failed to load budget lines.")));
    return () => {
      cancelled = true;
    };
  }, [setError]);

  const loadMatrix = useCallback(async () => {
    if (!budget?.id) return;
    const res = await getAllocationMatrix(propertyId, budget.id, {
      page,
      page_size: pageSize,
      search: debouncedSearch,
    });
    setMatrix(res.data);
  }, [propertyId, budget?.id, page, pageSize, debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    loadMatrix().catch((error) => {
      if (!cancelled) setError?.(getApiErrorMessage(error, "Failed to load who pays."));
    });
    return () => {
      cancelled = true;
    };
  }, [loadMatrix, setError]);

  useEffect(() => {
    setAmountEdits({});
    setShareEdits({});
    setRemovedSectionIds([]);
    setAddedSections([]);
    setMechanismEdits({});
    setPage(1);
  }, [budget?.id]);

  const sections = useMemo(() => {
    const saved = (budget?.sections || []).filter((section) => !removedSectionIds.includes(section.id));
    return [...saved, ...addedSections];
  }, [budget, removedSectionIds, addedSections]);

  const sectionTotal = useCallback(
    (section) => {
      if (String(section.id).startsWith("new-")) {
        return Object.entries(amountEdits).reduce((sum, [key, value]) => {
          if (!key.startsWith(`${section.id}:`)) return sum;
          return sum + (Number(value) || 0);
        }, 0);
      }
      const saved = (section.lines || []).reduce((sum, line) => {
        const key = editKey(section.id, line.id);
        const value = key in amountEdits ? amountEdits[key] : line.amount;
        return sum + (Number(value) || 0);
      }, 0);
      const extras = Object.entries(amountEdits).reduce((sum, [key, value]) => {
        if (!key.startsWith(`${section.id}:new-`)) return sum;
        return sum + (Number(value) || 0);
      }, 0);
      return saved + extras;
    },
    [amountEdits]
  );

  const dirtyCount = Object.keys(amountEdits).length + Object.keys(shareEdits).length + removedSectionIds.length + addedSections.length + Object.keys(mechanismEdits).length;

  useEffect(() => {
    onDirtyChange?.(dirtyCount > 0);
  }, [dirtyCount, onDirtyChange]);

  useEffect(() => {
    if (!scrollToWhoPays) return;
    document.getElementById("who-pays")?.scrollIntoView({ block: "start" });
  }, [scrollToWhoPays, matrix]);

  useEffect(() => {
    const onLeave = (event) => {
      if (!dirtyCount) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [dirtyCount]);

  const rows = useMemo(() => {
    const groups = new Map();
    const extras = [];
    (budget?.sections || []).forEach((section) => {
      (section.lines || []).forEach((line) => {
        if (line.line_item_id && catalogue.some((item) => item.id === line.line_item_id)) return;
        if (!extras.some((item) => item.id === line.id)) {
          extras.push({ id: `line-${line.id}`, lineId: line.id, name: line.name, category: line.category || "Other", account_code: line.account_code });
        }
      });
    });
    [...catalogue, ...extras].forEach((item) => {
      const category = item.category || "Other";
      if (!groups.has(category)) groups.set(category, []);
      groups.get(category).push(item);
    });
    return Array.from(groups, ([category, lines]) => ({
      category,
      lines: lines.sort((a, b) => (a.default_account_code || a.account_code || "").localeCompare(b.default_account_code || b.account_code || "") || a.name.localeCompare(b.name)),
    })).sort((a, b) => a.category.localeCompare(b.category));
  }, [catalogue, budget]);

  const amountFor = (section, item) => {
    const existing = (section.lines || []).find((line) => (item.lineId ? line.id === item.lineId : line.line_item_id === item.id));
    const key = existing ? editKey(section.id, existing.id) : editKey(section.id, `new-${item.id}`);
    if (key in amountEdits) return amountEdits[key];
    return existing ? String(existing.amount ?? "") : "";
  };

  const setAmount = (section, item, value) => {
    const existing = (section.lines || []).find((line) => (item.lineId ? line.id === item.lineId : line.line_item_id === item.id));
    const key = existing ? editKey(section.id, existing.id) : editKey(section.id, `new-${item.id}`);
    setAmountEdits((current) => ({ ...current, [key]: value }));
  };

  const savedPot = (section) => Number(matrix?.sections?.find((item) => item.id === section.id)?.allocated_amount) || 0;
  const sectionFunded = (section) => sectionTotal(section) > 0 || savedPot(section) > 0;

  const shareFor = (section, cell) => {
    const edit = shareEdits[shareKey(section.id, cell.unit_id)];
    const discretionary = section.effective_mechanism === "discretionary" || (!section.effective_mechanism && section.mechanism === "discretionary");
    if (!discretionary) {
      return { percentage: cell.percentage ?? "", amount: cell.amount ?? "", discretionary: false };
    }
    const shown = liveShare({
      percentage: cell.percentage,
      amount: cell.amount,
      edit: edit?.basis ? edit : null,
      sectionTotal: sectionTotal(section),
      savedPot: savedPot(section),
    });
    return { percentage: shown.percentage ?? "", amount: shown.amount ?? "", discretionary: true };
  };

  const setShare = (section, unitId, field, value, cell = {}) => {
    const key = shareKey(section.id, unitId);
    const nextValue = section.section_type === "estate" && value === "" ? "0" : value;
    const lines = sectionTotal(section);
    const base = lines > 0 ? lines : savedPot(section);
    const next = syncDiscretionary(nextValue, field, base);
    setShareEdits((edits) => {
      const current = edits[key] || {};
      return {
        ...edits,
        [key]: {
          ...next,
          reason: current.reason ?? cell.reason ?? "",
          savedAmount: current.savedAmount ?? cell.amount ?? "",
          savedPercentage: current.savedPercentage ?? cell.percentage ?? "",
        },
      };
    });
  };

  const setReason = (section, unitId, reason, cell = {}) => {
    const key = shareKey(section.id, unitId);
    setShareEdits((edits) => {
      const current = edits[key] || {
        percentage: cell.percentage ?? "",
        amount: cell.amount ?? "",
        savedAmount: cell.amount ?? "",
        savedPercentage: cell.percentage ?? "",
      };
      return { ...edits, [key]: { ...current, reason } };
    });
  };

  const chooseBudget = (id) => {
    if (dirtyCount && !window.confirm("You have unsaved changes. Leave this budget?")) return;
    onSelectBudget(id);
  };

  const buildSectionPayload = (section, index) => {
    const lines = [];
    rows.forEach((group) => {
      group.lines.forEach((item) => {
        const existing = (section.lines || []).find((line) => (item.lineId ? line.id === item.lineId : line.line_item_id === item.id));
        const key = existing ? editKey(section.id, existing.id) : editKey(section.id, `new-${item.id}`);
        const raw = key in amountEdits ? amountEdits[key] : existing ? existing.amount : "";
        if (raw === "" || raw == null) return;
        lines.push({
          id: existing?.id,
          name: item.name,
          line_item_id: item.lineId ? existing?.line_item_id : item.id,
          amount: Number(raw) || 0,
          previous_amount: existing?.previous_amount ?? null,
          sort_order: lines.length,
        });
      });
    });
    return {
      id: String(section.id).startsWith("new-") ? undefined : section.id,
      name: section.name,
      section_type: section.section_type || "block",
      mechanism: mechanismEdits[section.id] || section.mechanism || "discretionary",
      sort_order: index,
      lines,
    };
  };

  const exportPdf = async () => {
    if (!budget?.id || exporting || saving) return;
    if (dirtyCount) {
      setError?.("Save this budget before exporting. The PDF uses the last saved budget.");
      return;
    }
    setExporting(true);
    setError?.("");
    try {
      const response = await exportBudgetPdf(propertyId, budget.id);
      downloadBlob(response, "budget.pdf", "application/pdf");
      showToast?.("Budget PDF downloaded.");
    } catch (error) {
      setError?.(await exportErrorMessage(error, "Failed to export the budget."));
    } finally {
      setExporting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setFailedSectionId(null);
    setError?.("");
    try {
      const payload = {
        row_version: budget.row_version,
        name: budget.name,
        sections: sections.map(buildSectionPayload),
      };
      const saved = await updateBudget(propertyId, budget.id, payload);
      let current = saved.data;
      const warnings = current.rescale_warnings || [];
      for (const section of sections) {
        const savedSection = (current.sections || []).find((item) => item.name === section.name && item.section_type === (section.section_type || "block"));
        if (!savedSection) continue;
        const edits = Object.entries(shareEdits).filter(([key]) => key.startsWith(`${section.id}:`));
        if (!edits.length && !mechanismEdits[section.id]) continue;
        const discretionary = (savedSection.effective_mechanism || savedSection.mechanism) === "discretionary";
        try {
          if (discretionary) {
            const allocations = edits.map(([key, value]) => {
              const unitId = Number(key.split(":")[1]);
              const blank = (value.percentage === "" || value.percentage == null) && (value.amount === "" || value.amount == null);
              if (blank && section.section_type === "estate") {
                return { unit_id: unitId, percentage: "0", reason: value.reason || "" };
              }
              if (blank) return { unit_id: unitId, remove: true };
              const lineTotal = sectionTotal(section);
              const shown = liveShare({
                percentage: value.savedPercentage ?? value.percentage,
                amount: value.savedAmount ?? value.amount,
                edit: value.basis ? value : null,
                sectionTotal: lineTotal,
                savedPot: Number(matrix?.sections?.find((item) => item.id === section.id)?.allocated_amount) || 0,
              });
              if (lineTotal > 0) {
                return { unit_id: unitId, percentage: shown.percentage, reason: value.reason || "" };
              }
              return { unit_id: unitId, amount: shown.amount || "0", reason: value.reason || "" };
            });
            if (allocations.length) {
              await saveDiscretionaryAllocations(propertyId, budget.id, savedSection.id, {
                row_version: savedSection.row_version,
                budget_row_version: current.row_version,
                property_row_version: current.property_row_version,
                allocations,
              });
            }
          } else if (edits.length || mechanismEdits[section.id]) {
            const removeIds = edits.filter(([, value]) => value.included === false).map(([key]) => Number(key.split(":")[1]));
            const addIds = edits.filter(([, value]) => value.included === true).map(([key]) => Number(key.split(":")[1]));
            await updateSectionAllocations(propertyId, budget.id, savedSection.id, {
              row_version: savedSection.row_version,
              mechanism: mechanismEdits[section.id] || savedSection.mechanism,
              ...(removeIds.length || addIds.length ? { add_unit_ids: addIds, remove_unit_ids: removeIds } : {}),
            });
          }
        } catch (error) {
          setFailedSectionId(savedSection.id);
          throw error;
        }
      }
      setAmountEdits({});
      setShareEdits({});
      setRemovedSectionIds([]);
      setAddedSections([]);
      setMechanismEdits({});
      await onReload?.(budget.id);
      await loadMatrix();
      showToast?.(warnings[0] || "Budget saved.");
    } catch (error) {
      if (isConcurrencyConflict(error)) {
        setError?.("This budget was changed elsewhere. Reloading…");
        await onReload?.(budget.id);
      } else {
        setError?.(getApiErrorMessage(error, "Failed to save the budget."));
      }
    } finally {
      setSaving(false);
    }
  };

  const applyMechanism = async (section, mechanism) => {
    if (section.section_type === "estate") {
      setEstateConfirm({ section, mechanism });
      return;
    }
    setMechanismEdits((current) => ({ ...current, [section.id]: mechanism }));
    setMenu(null);
  };

  const confirmEstateMechanism = async () => {
    try {
      await updateServiceChargeMechanism(propertyId, {
        service_charge_mechanism: estateConfirm.mechanism,
        row_version: budget.property_row_version,
      });
      setEstateConfirm(null);
      showToast?.("Estate split updated for this property.");
      await onReload?.(budget.id);
      await loadMatrix();
    } catch (error) {
      setError?.(getApiErrorMessage(error, "Failed to update the estate split."));
    }
  };

  const amountColCount = 1 + sections.length * (showPrevious ? 2 : 1);
  const shareSpanFor = (section) => (sectionFunded(section) ? (showPrevious ? 3 : 2) : 1);
  const shareColCount = 2 + sections.reduce((sum, section) => sum + shareSpanFor(section), 0);
  const anyFunded = sections.some(sectionFunded);

  const amountColumns = (
    <>
      <Box component="col" sx={{ width: NAME_WIDTH }} />
      {sections.map((section) => (
        <React.Fragment key={section.id}>
          {showPrevious && <Box component="col" sx={{ width: PREV_WIDTH }} />}
          <Box component="col" sx={{ width: AMOUNT_WIDTH }} />
        </React.Fragment>
      ))}
    </>
  );

  const shareColumns = (
    <>
      <Box component="col" sx={{ width: NAME_WIDTH }} />
      {sections.map((section) => (
        sectionFunded(section) ? (
          <React.Fragment key={section.id}>
            {showPrevious && <Box component="col" sx={{ width: PREV_WIDTH }} />}
            <Box component="col" sx={{ width: PERCENT_WIDTH }} />
            <Box component="col" sx={{ width: POUND_WIDTH }} />
          </React.Fragment>
        ) : (
          <Box component="col" key={section.id} sx={{ width: QUIET_WIDTH }} />
        )
      ))}
      <Box component="col" sx={{ width: TOTAL_WIDTH }} />
    </>
  );

  const liveDiscretionary = (section, header) => {
    const lines = sectionTotal(section);
    const pot = Number(header?.allocated_amount) || 0;
    const total = lines > 0 ? lines : pot;
    const savedPct = lines > 0 ? (Number(header?.allocated_percentage) || 0) : (pot > 0 ? 100 : 0);
    let amount = (savedPct / 100) * total;
    Object.entries(shareEdits).forEach(([key, edit]) => {
      if (!key.startsWith(`${section.id}:`) || !edit.basis) return;
      const shown = liveShare({
        percentage: edit.savedPercentage,
        amount: edit.savedAmount,
        edit,
        sectionTotal: lines,
        savedPot: pot,
      });
      const baseline = lines > 0
        ? ((Number(edit.savedPercentage) || 0) / 100) * lines
        : Number(edit.savedAmount) || 0;
      amount += (Number(shown.amount) || 0) - baseline;
    });
    const percentage = total > 0 ? (amount / total) * 100 : 0;
    return { amount, percentage };
  };

  let amountRow = 0;
  const budgetTotal = sections.reduce((sum, section) => sum + sectionTotal(section), 0);
  const allocationNotes = sections.flatMap((section) => {
    const header = matrix?.sections?.find((item) => item.id === section.id);
    if (!header) return [];
    const discretionary = (mechanismEdits[section.id] || header.effective_mechanism || section.mechanism) === "discretionary";
    const pct = discretionary ? liveDiscretionary(section, header).percentage : Number(header.allocated_percentage);
    const lines = sectionTotal(section);
    const pot = Number(header.allocated_amount) || 0;
    if ((lines <= 0 && pot <= 0) || Math.abs(pct - 100) <= 0.5) return [];
    return [`${section.name} is ${pct.toFixed(1)}% allocated`];
  });

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end", flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Budget</InputLabel>
            <Select
              label="Budget"
              value={budget ? String(budget.id) : ""}
              onChange={(event) => chooseBudget(event.target.value)}
            >
              {budgets.map((item) => (
                <MenuItem key={item.id} value={String(item.id)}>
                  {item.name} · {fiscalYearLabel(item.fiscal_year) || "No year"} · {budgetStatusLabel(item.status)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={onCreate} sx={{ mb: 0.25 }}>Create budget</Button>
          {budget && <Chip size="small" label={budgetStatusLabel(budget.status)} color={budgetStatusColor(budget.status)} sx={{ mb: 0.5 }} />}
          <Box sx={{ pb: 0.25 }}>
            <Eyebrow sx={{ display: "block", mb: 0.5 }}>Budget total</Eyebrow>
            <Box sx={{ fontFamily: fonts.serif, fontSize: 28, fontWeight: 600, lineHeight: 1, letterSpacing: "-0.02em" }}>
              {money(budgetTotal)}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end", alignItems: "center" }}>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} disabled={readOnly}>Add block charge</Button>
          {budget?.status === "draft" && (
            <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => onDelete(budget)}>Delete</Button>
          )}
          <Button
            size="small"
            variant="contained"
            disabled={readOnly || saving || !dirtyCount}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={save}
          >
            {dirtyCount ? `Save (${dirtyCount} ${dirtyCount === 1 ? "change" : "changes"})` : "Save"}
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={saving || exporting}
            startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={exportPdf}
          >
            {exporting ? "Exporting…" : "Export PDF"}
          </Button>
          {(budget?.status === "draft" || budget?.status === "approved" || budget?.status === "published") && (
            <Box sx={{ width: "1px", alignSelf: "stretch", bgcolor: "brand.border", mx: 0.5 }} />
          )}
          {budget?.status === "draft" && (
            <Button size="small" variant="outlined" startIcon={<CheckIcon />} onClick={() => onStatus("approve")}>Approve</Button>
          )}
          {budget?.status === "approved" && (
            <Button size="small" variant="outlined" startIcon={<DoneAllIcon />} onClick={() => onStatus("publish")}>Publish</Button>
          )}
          {(budget?.status === "approved" || budget?.status === "published") && (
            <Button
              size="small"
              variant="outlined"
              disabled={visibilitySaving}
              startIcon={budget.visible_to_owners ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={async () => {
                setVisibilitySaving(true);
                try {
                  await onOwnerVisibility(!budget.visible_to_owners);
                } finally {
                  setVisibilitySaving(false);
                }
              }}
            >
              {budget.visible_to_owners ? "Hide from owners" : "Show to owners"}
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 1.5, mb: allocationNotes.length ? 1 : 2 }}>
        {sections.map((section) => {
          const total = sectionTotal(section);
          const header = matrix?.sections?.find((item) => item.id === section.id);
          const discretionary = (mechanismEdits[section.id] || header?.effective_mechanism || section.effective_mechanism || section.mechanism) === "discretionary";
          const live = header && discretionary ? liveDiscretionary(section, header) : null;
          const allocatedPct = live ? live.percentage : header ? Number(header.allocated_percentage) : null;
          const pot = Number(header?.allocated_amount) || 0;
          const short = (total > 0 || pot > 0) && allocatedPct != null && Math.abs(allocatedPct - 100) > 0.5;
          const failed = failedSectionId === section.id;
          return (
            <Box
              key={section.id}
              sx={{
                position: "relative",
                bgcolor: failed || short ? "brand.goldTint" : "background.paper",
                border: "1px solid",
                borderColor: failed ? "error.main" : short ? "brand.gold" : "brand.border",
                borderRadius: "8px",
                px: 2,
                py: 1.5,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{section.name}</Typography>
                <IconButton size="small" aria-label={`${section.name} actions`} onClick={(event) => setMenu({ anchor: event.currentTarget, section })} sx={{ mt: -0.75, mr: -0.75 }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ fontFamily: fonts.serif, fontSize: 26, fontWeight: 600, lineHeight: 1, letterSpacing: "-0.02em", mt: 1 }}>
                {money(total)}
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 500, mt: 0.75, color: "brand.inkSoft" }}>
                {mechanismLabel(mechanismEdits[section.id] || section.effective_mechanism || section.mechanism)}
                {header && " · "}
                {header
                  ? (total > 0 || pot > 0)
                    ? (
                      <Box
                        component="a"
                        href={`#who-${section.id}`}
                        onClick={(event) => scrollToId(event, `who-${section.id}`)}
                        sx={{ color: short || failed ? "brand.goldDeep" : "brand.inkSoft", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                      >
                        {`${header.unit_count} ${header.unit_count === 1 ? "unit" : "units"} · ${Number(allocatedPct).toFixed(0)}% allocated`}
                      </Box>
                    )
                    : (
                      <Box
                        component="a"
                        href={`#amount-${section.id}`}
                        onClick={(event) => scrollToId(event, `amount-${section.id}`)}
                        sx={{ color: "brand.goldDeep", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                      >
                        Enter the charge
                      </Box>
                    )
                  : "Units appear after you save"}
              </Typography>
            </Box>
          );
        })}
      </Box>
      {allocationNotes.length > 0 && (
        <Typography sx={{ fontSize: 13, color: "brand.goldDeep", fontWeight: 500, mb: 2 }}>
          {allocationNotes.join(" · ")}
        </Typography>
      )}

      <Box sx={{ border: "1px solid", borderColor: "brand.border", borderRadius: "8px", bgcolor: "background.paper", overflow: "hidden", mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, px: 2.5, pt: 2, pb: 1 }}>
          <Eyebrow>Amounts</Eyebrow>
          <Button size="small" variant={showPrevious ? "contained" : "outlined"} startIcon={<HistoryIcon />} onClick={onTogglePrevious}>
            {showPrevious ? "Hide previous year" : "Show previous year"}
          </Button>
        </Box>
        <Box sx={{ overflow: "auto", maxHeight: "min(68vh, 720px)" }}>
        <DataTable size="small" sx={{ ...gridTableSx, tableLayout: "fixed", minWidth: NAME_WIDTH + sections.length * ((showPrevious ? PREV_WIDTH : 0) + AMOUNT_WIDTH) }}>
          <colgroup>{amountColumns}</colgroup>
          <Box component="thead">
            {showPrevious ? (
              <>
                <Box component="tr" className="matrix-group">
                  <Box component="th" rowSpan={2} className="matrix-start" sx={{ position: "sticky", left: 0, zIndex: 4 }}>Line</Box>
                  {sections.map((section) => (
                    <Box component="th" key={section.id} id={`amount-${section.id}`} colSpan={2} className="matrix-split">{section.name}</Box>
                  ))}
                </Box>
                <Box component="tr" className="matrix-subhead">
                  {sections.map((section) => (
                    <React.Fragment key={section.id}>
                      <Box component="th" className="matrix-split matrix-sub">Previous</Box>
                      <Box component="th" className="matrix-sub matrix-end">Amount</Box>
                    </React.Fragment>
                  ))}
                </Box>
              </>
            ) : (
              <Box component="tr">
                <Box component="th" className="matrix-start" sx={{ position: "sticky", left: 0, zIndex: 4 }}>Line</Box>
                {sections.map((section) => (
                  <Box component="th" key={section.id} id={`amount-${section.id}`} className="matrix-split matrix-end">{section.name}</Box>
                ))}
              </Box>
            )}
          </Box>
          <Box component="tbody">
            {rows.map((group) => {
              const categoryOpen = !collapsedCategories.has(group.category);
              return (
              <React.Fragment key={group.category}>
                <Box component="tr">
                  <Box component="td" className="matrix-category" colSpan={amountColCount}>
                    <Box
                      component="button"
                      type="button"
                      className="matrix-category-toggle"
                      aria-expanded={categoryOpen}
                      onClick={() => setCollapsedCategories((current) => {
                        const next = new Set(current);
                        if (next.has(group.category)) next.delete(group.category);
                        else next.add(group.category);
                        return next;
                      })}
                    >
                      <Box component="span" className="matrix-category-label">
                        <ExpandMoreIcon sx={{ fontSize: 16, transform: categoryOpen ? "none" : "rotate(-90deg)", transition: "transform 120ms ease" }} />
                        {group.category}
                        <Box component="span" sx={{ fontWeight: 500, letterSpacing: 0, textTransform: "none" }}>
                          {group.lines.length}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                {categoryOpen && group.lines.map((item) => {
                  const rowIndex = amountRow;
                  amountRow += 1;
                  const accountCode = item.default_account_code || item.account_code;
                  return (
                    <Box component="tr" key={item.id}>
                      <Box component="td" className="matrix-name">
                        {accountCode && (
                          <Box component="span" sx={{ mr: 1, fontFamily: fonts.mono, fontSize: 11, color: "brand.inkSoft" }}>{accountCode}</Box>
                        )}
                        {item.name}
                      </Box>
                      {sections.map((section) => {
                        const existing = (section.lines || []).find((line) => (item.lineId ? line.id === item.lineId : line.line_item_id === item.id));
                        const key = existing ? editKey(section.id, existing.id) : editKey(section.id, `new-${item.id}`);
                        return (
                          <React.Fragment key={section.id}>
                            {showPrevious && (
                              <Box component="td" className="matrix-split" align="right" sx={{ color: "brand.inkSoft", fontVariantNumeric: "tabular-nums" }}>
                                {money(existing?.previous_amount)}
                              </Box>
                            )}
                            <Box
                              component="td"
                              className={showPrevious ? undefined : "matrix-split"}
                              align="right"
                              onMouseDown={(event) => {
                                if (readOnly || event.target.closest("input")) return;
                                event.preventDefault();
                                event.currentTarget.querySelector("input")?.focus();
                              }}
                              sx={{ cursor: readOnly ? undefined : "text" }}
                            >
                              <MoneyCell
                                value={amountFor(section, item)}
                                label={`${item.name}, ${section.name}, amount`}
                                readOnly={readOnly}
                                unsaved={key in amountEdits}
                                blankZero
                                quietBlank
                                row={rowIndex}
                                col={`amount-${section.id}`}
                                onKeyDown={moveGridFocus}
                                onChange={(value) => setAmount(section, item, value)}
                              />
                            </Box>
                          </React.Fragment>
                        );
                      })}
                    </Box>
                  );
                })}
              </React.Fragment>
              );
            })}
          </Box>
          <Box component="tfoot">
            <Box component="tr" className="matrix-total">
              <Box component="td" className="matrix-name">Total</Box>
              {sections.map((section) => {
                const total = sectionTotal(section);
                const previous = (section.lines || []).reduce((sum, line) => sum + (Number(line.previous_amount) || 0), 0);
                return (
                  <React.Fragment key={section.id}>
                    {showPrevious && (
                      <Box component="td" className="matrix-split" align="right" sx={{ color: "brand.inkSoft", fontVariantNumeric: "tabular-nums" }}>
                        {money(previous)}
                      </Box>
                    )}
                    <Box component="td" className={showPrevious ? undefined : "matrix-split"} align="right" sx={{ fontVariantNumeric: "tabular-nums", color: total ? "brand.ink" : "brand.inkSoft" }}>
                      {money(total)}
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
        </DataTable>
        </Box>
      </Box>

      <Box id="who-pays" sx={{ border: "1px solid", borderColor: "brand.border", borderRadius: "8px", bgcolor: "background.paper", overflow: "hidden" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, px: 2.5, py: 1.75 }}>
          <Eyebrow>Who pays</Eyebrow>
          <TextField size="small" placeholder="Search units" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} aria-label="Search units" sx={{ width: 240 }} />
        </Box>
        <Box sx={{ overflow: "auto" }}>
        <DataTable size="small" sx={{ ...gridTableSx, tableLayout: "fixed", minWidth: NAME_WIDTH + TOTAL_WIDTH + sections.reduce((sum, section) => sum + (sectionFunded(section) ? (showPrevious ? PREV_WIDTH : 0) + PERCENT_WIDTH + POUND_WIDTH : QUIET_WIDTH), 0) }}>
          <colgroup>{shareColumns}</colgroup>
          <Box component="thead">
            <Box component="tr" className="matrix-group">
              <Box component="th" rowSpan={2} className="matrix-start" sx={{ position: "sticky", left: 0, zIndex: 4 }}>Unit</Box>
              {sections.map((section) => (
                sectionFunded(section) ? (
                  <Box component="th" key={section.id} id={`who-${section.id}`} colSpan={shareSpanFor(section)} className="matrix-split">{section.name}</Box>
                ) : (
                  <Box component="th" key={section.id} id={`who-${section.id}`} rowSpan={2} className="matrix-split">
                    {section.name}
                    <Box component="span" sx={{ display: "block", mt: 0.25, fontSize: 11, fontWeight: 500, color: "brand.inkSoft" }}>No charge</Box>
                  </Box>
                )
              ))}
              <Box component="th" rowSpan={2} className="matrix-split matrix-end">Total</Box>
            </Box>
            {anyFunded && (
            <Box component="tr" className="matrix-subhead">
              {sections.map((section) => (
                sectionFunded(section) ? (
                  <React.Fragment key={section.id}>
                    {showPrevious && <Box component="th" className="matrix-split matrix-sub">Previous</Box>}
                    <Box component="th" className={`${showPrevious ? "" : "matrix-split "}matrix-sub`}>Percent</Box>
                    <Box component="th" className="matrix-sub">Pounds</Box>
                  </React.Fragment>
                ) : null
              ))}
            </Box>
            )}
          </Box>
          <Box component="tbody">
            {(matrix?.results || []).length === 0 && (
              <Box component="tr">
                <Box component="td" colSpan={shareColCount} sx={{ color: "brand.inkSoft", py: 2 }}>
                  {matrix ? "No units match this search." : "Loading units…"}
                </Box>
              </Box>
            )}
            {(matrix?.results || []).map((unit, rowIndex) => {
              const shares = sections.map((section) => {
                const cell = (unit.cells || []).find((item) => item.section_id === section.id) || { unit_id: unit.unit_id, percentage: "", amount: "", included: false };
                return { section, cell, share: shareFor(section, { ...cell, unit_id: unit.unit_id }) };
              });
              const unitTotal = shares.reduce((sum, item) => sum + (Number(item.share.amount) || 0), 0);
              return (
              <Box component="tr" key={unit.unit_id}>
                <Box component="td" className="matrix-name">{unit.unit_name}</Box>
                {shares.map(({ section, cell, share }) => {
                  if (!sectionFunded(section)) {
                    return (
                      <Box component="td" key={section.id} className="matrix-split" align="center" sx={{ color: "brand.inkSoft" }}>—</Box>
                    );
                  }
                  const discretionary = share.discretionary;
                  const reason = shareEdits[shareKey(section.id, unit.unit_id)]?.reason ?? cell.reason;
                  return (
                    <React.Fragment key={section.id}>
                      {showPrevious && <Box component="td" className="matrix-split" />}
                      <Box component="td" className={showPrevious ? undefined : "matrix-split"} align="right">
                        {discretionary ? (
                          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.25 }}>
                            <PercentCell
                              align="center"
                              value={share.percentage}
                              label={`${unit.unit_name}, ${section.name}, percentage`}
                              readOnly={readOnly}
                              row={rowIndex}
                              col={`pct-${section.id}`}
                              onKeyDown={moveGridFocus}
                              onChange={(value) => setShare(section, unit.unit_id, "percentage", value, cell)}
                            />
                            {!readOnly && (
                              <IconButton
                                size="small"
                                aria-label={`Reason for ${unit.unit_name}, ${section.name}`}
                                onClick={() => {
                                  const current = shareEdits[shareKey(section.id, unit.unit_id)];
                                  setReasonText(current?.reason ?? cell.reason ?? "");
                                  setReasonEdit({ section, unitId: unit.unit_id, cell });
                                }}
                                sx={{ color: reason ? "brand.goldDeep" : "brand.inkSoft" }}
                              >
                                <CommentIcon fontSize="inherit" />
                              </IconButton>
                            )}
                          </Box>
                        ) : (
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <Checkbox
                              size="small"
                              checked={shareEdits[shareKey(section.id, unit.unit_id)]?.included ?? cell.included}
                              disabled={readOnly || section.section_type === "estate"}
                              inputProps={{ "aria-label": `Include ${unit.unit_name} in ${section.name}` }}
                              onChange={(event) => setShareEdits((current) => ({
                                ...current,
                                [shareKey(section.id, unit.unit_id)]: { included: event.target.checked },
                              }))}
                            />
                            <Typography sx={{ fontSize: 11, color: "brand.inkSoft" }}>
                              {cell.percentage != null ? `${Number(cell.percentage).toFixed(2)}% calculated` : "Updates on save"}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Box component="td" align="right">
                        {discretionary ? (
                          <MoneyCell
                            align="center"
                            value={share.amount}
                            placeholder={sectionTotal(section) <= 0 && savedPot(section) <= 0 ? "—" : "0.00"}
                            label={`${unit.unit_name}, ${section.name}, amount`}
                            readOnly={readOnly}
                            unsaved={Boolean(shareEdits[shareKey(section.id, unit.unit_id)])}
                            row={rowIndex}
                            col={`gbp-${section.id}`}
                            onKeyDown={moveGridFocus}
                            onChange={(value) => setShare(section, unit.unit_id, "amount", value, cell)}
                          />
                        ) : (
                          <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>{money(cell.amount)}</Box>
                        )}
                      </Box>
                    </React.Fragment>
                  );
                })}
                <Box component="td" className="matrix-split" align="right">
                  <Box component="span" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums", color: unitTotal ? "brand.ink" : "brand.inkSoft" }}>{money(unitTotal)}</Box>
                </Box>
              </Box>
              );
            })}
          </Box>
        </DataTable>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, px: 2.5, py: 1.25, borderTop: "1px solid", borderColor: "brand.border", bgcolor: "brand.surfaceMuted" }}>
          <Typography variant="caption" sx={{ color: "brand.inkSoft" }}>
            Page {matrix?.page || 1} of {Math.max(1, Math.ceil((matrix?.total || 0) / pageSize))}
          </Typography>
          <Pagination
            size="small"
            count={Math.max(1, Math.ceil((matrix?.total || 0) / pageSize))}
            page={page}
            onChange={(_, value) => setPage(value)}
          />
          <Select size="small" value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }} aria-label="Rows per page">
            {[10, 25, 50].map((size) => <MenuItem key={size} value={size}>{size} rows</MenuItem>)}
          </Select>
        </Box>
      </Box>

      <Menu open={Boolean(menu)} anchorEl={menu?.anchor} onClose={() => setMenu(null)}>
        {MECHANISM_OPTIONS.map((option) => (
          <MenuItem key={option.value} onClick={() => applyMechanism(menu.section, option.value)}>{option.label}</MenuItem>
        ))}
        {menu?.section && menu.section.section_type !== "estate" && !String(menu.section.id).startsWith("new-") && (
          <MenuItem onClick={() => { setGroupOpen(true); getUnitGroups(propertyId).then((data) => setGroups(Array.isArray(data) ? data : data.results || [])).catch(() => setGroups([])); }}>
            + Add from unit group
          </MenuItem>
        )}
        {menu?.section && menu.section.section_type !== "estate" && !String(menu.section.id).startsWith("new-") && (
          <MenuItem
            disabled={!debouncedSearch}
            onClick={async () => {
              const section = menu.section;
              setMenu(null);
              if (dirtyCount) {
                setError?.("Save the budget before changing who is included in bulk.");
                return;
              }
              try {
                await updateSectionAllocations(propertyId, budget.id, section.id, {
                  row_version: section.row_version,
                  include_search: debouncedSearch,
                });
                await loadMatrix();
                showToast?.("Matching units included.");
              } catch (error) {
                setError?.(getApiErrorMessage(error, "Failed to include matching units."));
              }
            }}
          >
            Include all matching search
          </MenuItem>
        )}
        {menu?.section && menu.section.section_type !== "estate" && !String(menu.section.id).startsWith("new-") && (
          <MenuItem onClick={() => { setRemoveAllSection(menu.section); setMenu(null); }}>Remove all</MenuItem>
        )}
        {menu?.section && menu.section.section_type !== "estate" && (
          <MenuItem onClick={() => { setDeleteSection(menu.section); setMenu(null); }}>Delete block charge</MenuItem>
        )}
      </Menu>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)}>
        <DialogTitle>Add block charge</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Block name" value={blockName} onChange={(event) => setBlockName(event.target.value)} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CloseIcon />} onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => {
            const name = blockName.trim();
            if (!name) return;
            setAddedSections((current) => [...current, { id: `new-${Date.now()}`, name, section_type: "block", mechanism: "discretionary", lines: [] }]);
            setBlockName("");
            setAddOpen(false);
          }}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={groupOpen} onClose={() => setGroupOpen(false)}>
        <DialogTitle>Add from unit group</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Unit group</InputLabel>
            <Select label="Unit group" value={groupId} onChange={(event) => setGroupId(event.target.value)}>
              {groups.map((group) => <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CloseIcon />} onClick={() => setGroupOpen(false)}>Cancel</Button>
          <Button startIcon={<AddIcon />} variant="contained" onClick={async () => {
            if (!groupId || !menu?.section) return;
            try {
              await updateSectionAllocations(propertyId, budget.id, menu.section.id, {
                row_version: menu.section.row_version,
                add_group_id: groupId,
              });
              setGroupOpen(false);
              setMenu(null);
              await loadMatrix();
              showToast?.("Units added from the group.");
            } catch (error) {
              setError?.(getApiErrorMessage(error, "Failed to add the unit group."));
            }
          }}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(estateConfirm)} onClose={() => setEstateConfirm(null)}>
        <DialogTitle>Change the estate split?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This changes the estate split for every budget on this property, because invoices use the property setting.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CloseIcon />} onClick={() => setEstateConfirm(null)}>Cancel</Button>
          <Button startIcon={<CheckIcon />} variant="contained" onClick={confirmEstateMechanism}>Change split</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(reasonEdit)} onClose={() => setReasonEdit(null)}>
        <DialogTitle>Share reason</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason"
            value={reasonText}
            onChange={(event) => setReasonText(event.target.value)}
            fullWidth
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CloseIcon />} onClick={() => setReasonEdit(null)}>Cancel</Button>
          <Button
            startIcon={<CheckIcon />}
            variant="contained"
            onClick={() => {
              setReason(reasonEdit.section, reasonEdit.unitId, reasonText, reasonEdit.cell);
              setReasonEdit(null);
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteSection)} onClose={() => setDeleteSection(null)}>
        <DialogTitle>Delete this block charge?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Remove ${deleteSection?.name || "this block"} from the budget when you save. Lines that supplier bills still use cannot be deleted.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CloseIcon />} onClick={() => setDeleteSection(null)}>Cancel</Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={() => {
              if (String(deleteSection.id).startsWith("new-")) {
                setAddedSections((current) => current.filter((section) => section.id !== deleteSection.id));
              } else {
                setRemovedSectionIds((current) => [...current, deleteSection.id]);
              }
              setDeleteSection(null);
            }}
          >
            Delete block
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(removeAllSection)} onClose={() => setRemoveAllSection(null)}>
        <DialogTitle>Remove every unit?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Take every unit out of ${removeAllSection?.name || "this block"}. This is saved immediately.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CloseIcon />} onClick={() => setRemoveAllSection(null)}>Cancel</Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={async () => {
              const section = removeAllSection;
              setRemoveAllSection(null);
              if (dirtyCount) {
                setError?.("Save the budget before changing who is included in bulk.");
                return;
              }
              try {
                await updateSectionAllocations(propertyId, budget.id, section.id, {
                  row_version: section.row_version,
                  remove_all: true,
                });
                await loadMatrix();
                showToast?.("Units removed.");
              } catch (error) {
                setError?.(getApiErrorMessage(error, "Failed to remove units."));
              }
            }}
          >
            Remove all
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
